<script lang='ts'>

    /* IMPORTS --------------------------------------------------------------------------------- */

    // CASSIOPEIA
    import {
        BrandSelectMultiple,
        Button,
        CategoryTags,
        Heading,
        OrganizationForm,
        Paragraph,
        SelectChannels,
        SelectMarkets,
        SelectOrganizationTypes,
        SESSION_USER,
        SESSION_ORGANIZATION,
        SubscriptionForm,
        SUBSCRIPTION_API,
        InputToggle,
        BrandSimilarTags,
    } from '@smatch/cassiopeia-web-components';

    // COMPONENTS
    import OnboardingProgressLine from '../../components/OnboardingProgressLine.svelte';

    // PLUGINS
    import { onMount, type SvelteComponent } from 'svelte';
    import { useNavigate } from 'svelte-navigator';
    const navigate = useNavigate();

    // CONFIG
    import { DEFAULT, type Default } from './config.js';

    // UTILITY
    import merge from 'deepmerge';

    // TYPES
    import type { Subscription } from 'src/app/types';
    import type { Writable } from 'svelte/store';

    /* TYPES ----------------------------------------------------------------------------------- */

    interface SubscriptionForm extends SvelteComponent {
        submit(): Promise<void>;
    }

    /* INITIALIZE ------------------------------------------------------------------------------ */

    const overwrite = (destinationArray: [], sourceArray: []): [] => sourceArray;

    const ORGANIZATION = merge.all([ DEFAULT, ($SESSION_ORGANIZATION ?? {}) ], { arrayMerge: overwrite }) as Default;

    /* INTERNALS -------------------------------------------------------------------------------- */

    let subscriptionForm: SubscriptionForm | null = null;
    $: SUBSCRIPTION = subscriptionForm === null ? undefined : subscriptionForm.DATA as Writable<Subscription>;

    /*
     * Organization is not a wholesaler:
     * - if there is at least one type selected
     * - and the wholesaler is not the only type selected
     */
    $: isNotOnlyWholesaler = ORGANIZATION.types.includes('buyer_wholesaler')
        ? ORGANIZATION.types.length > 1
        : ORGANIZATION.types.length > 0;

    const onSave = async (): Promise<void> => {
        if ($SUBSCRIPTION === undefined || $SESSION_USER === null) {
            return;
        }

        $SUBSCRIPTION.ownerKey = $SESSION_USER._.key;
        await subscriptionForm!.submit();

        // For multiple brands, the create endpoint will only return a 204, which doesn't update the SUBSCRIPTION_STORE (and therefore makes transition to the next step impossible)
        void SUBSCRIPTION_API.filterSubscriptions({ ownerKey: $SESSION_USER._.key });
    };

    onMount(() => {
        if ($SESSION_USER === null) {
            navigate('/');
        }
    });

    const ALLOWS_SIMILAR_BRANDS: boolean = $SUBSCRIPTION?.constraints.allowsSimilarBrands ?? false;

    let isSimilarBrandsChecked = ALLOWS_SIMILAR_BRANDS;


    $: if ($SUBSCRIPTION !== undefined) {
        $SUBSCRIPTION.constraints.allowsSimilarBrands = isSimilarBrandsChecked;
    }

    $: if ($SUBSCRIPTION?.constraints.brandKeys?.length === 0) {
        isSimilarBrandsChecked = false;
        $SUBSCRIPTION.constraints.allowsSimilarBrands = false;
    }

</script>

{#if $SESSION_USER !== null}

    <Heading size = 'xl'>Hey { $SESSION_USER.name.first }</Heading>
    <Heading size = 'md' style = 'subtle'>We're so excited to meet you!</Heading>

    <Paragraph>Please describe your preferences, so we can we tailor our offers to best suit your needs.</Paragraph>

    <OnboardingProgressLine current = 'step2' />

    <OrganizationForm action = 'update' organization = { ORGANIZATION } on:save = { onSave }>

        <SelectOrganizationTypes
            bind:keys = { ORGANIZATION.types }
            label = { null }
            placeholder = 'What best describes your business?'
            domain = 'buyer'
        />

        {#if isNotOnlyWholesaler}

            <SelectChannels
                label = 'On which channels do you sell?'
                bind:keys = { ORGANIZATION.channelCodes }
            />

            <SelectMarkets
                label = 'On which markets do you sell?'
                bind:keys = { ORGANIZATION.marketCodes }
                minItems = { 1 }
            />

        {/if}

        <SubscriptionForm action = 'create' bind:this = { subscriptionForm }>

            {#if $SUBSCRIPTION !== undefined }

                <Paragraph>Which fashion categories are you interested in?</Paragraph>
                <CategoryTags
                    bind:keys = { $SUBSCRIPTION.constraints.categoryKeys }
                    label = { null }
                    verticalCode = { $SUBSCRIPTION.constraints.verticalCode }
                />
                {#key $SUBSCRIPTION.constraints.brandKeys?.length}
                    <BrandSelectMultiple
                        bind:keys = { $SUBSCRIPTION.constraints.brandKeys }
                        placeholder = 'Which brands are you interested in?'
                        verticalCode = { $SUBSCRIPTION.constraints.verticalCode }
                        label = { null }
                    />
                {/key}
                <BrandSimilarTags
                    bind:keys = { $SUBSCRIPTION.constraints.brandKeys }
                    parameters = {{ similarToBrandKeys: $SUBSCRIPTION.constraints.brandKeys }}
                />
                {#if $SUBSCRIPTION.constraints.brandKeys && $SUBSCRIPTION.constraints.brandKeys.length > 0}
                    <InputToggle
                        bind:isChecked={ isSimilarBrandsChecked }
                        name = 'subscription.similarBrands'
                        css = 'mb-4'
                        label = 'Notify me with deals that include brands similar to my selection.'
                    />
                {/if}
            {/if}

        </SubscriptionForm>

        <Button
            type = 'submit'
            size = 'lg'
            label = 'Continue'
            css = 'w-full'
        />

    </OrganizationForm>

{/if}